import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { LogIn, Mail, Users, Eye, Search, Monitor, Smartphone, Tablet, X } from "lucide-react";

// Formatter helpers ----------------------------------------------------------
const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};
const relTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso).getTime();
  const diff = Math.floor((Date.now() - d) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
};
const KindBadge = ({ kind, success }) => {
  const map = {
    login: success ? "border-emerald-500/40 text-emerald-400" : "border-bdu-red/40 text-bdu-red",
    login_failed: "border-bdu-red/40 text-bdu-red",
    register: "border-bdu-gold/40 text-bdu-gold",
    logout: "border-bdu-cream/20 text-bdu-cream/60",
  };
  return (
    <span className={`text-[0.55rem] uppercase tracking-widest px-2 py-0.5 border ${map[kind] || "border-bdu-cream/20 text-bdu-cream/60"}`}>
      {kind.replace("_", " ")}
    </span>
  );
};
const DeviceIcon = ({ device, size = 14 }) => {
  if (device === "mobile") return <Smartphone size={size} className="text-bdu-cream/60" />;
  if (device === "tablet") return <Tablet size={size} className="text-bdu-cream/60" />;
  return <Monitor size={size} className="text-bdu-cream/60" />;
};

const SourceLabel = ({ source }) => (
  <span className="text-[0.6rem] uppercase tracking-widest border border-bdu-gold/30 text-bdu-gold/90 px-2 py-0.5 whitespace-nowrap">
    {(source || "").replace(/_/g, " ")}
  </span>
);

// --- Main tab --------------------------------------------------------------
export default function VisitorsTab() {
  const [pane, setPane] = useState("logins"); // logins | captures | anonymous

  return (
    <div className="space-y-6" data-testid="admin-visitors-tab">
      <div className="border-b border-bdu-gold/10 flex gap-1 flex-wrap">
        {[
          ["logins", "Login Activity", <LogIn size={14} />],
          ["captures", "Email Captures", <Mail size={14} />],
          ["anonymous", "Anonymous Visitors", <Users size={14} />],
        ].map(([k, label, icon]) => (
          <button
            key={k}
            onClick={() => setPane(k)}
            data-testid={`visitors-subtab-${k}`}
            className={`px-4 py-2.5 text-[0.65rem] uppercase tracking-[0.2em] font-semibold border-b-2 -mb-px inline-flex items-center gap-2 transition-colors ${
              pane === k ? "border-bdu-gold text-bdu-gold" : "border-transparent text-bdu-cream/60 hover:text-bdu-cream"
            }`}
          >
            {icon}
            {label}
          </button>
        ))}
      </div>

      {pane === "logins" && <LoginsPane />}
      {pane === "captures" && <CapturesPane />}
      {pane === "anonymous" && <AnonymousPane />}
    </div>
  );
}

// --- Login activity pane ---------------------------------------------------
function LoginsPane() {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("all"); // all | login | login_failed | register
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    const params = filter === "all" ? {} : { kind: filter };
    api.get("/admin/visitors/logins", { params, headers: { "Cache-Control": "no-cache" } })
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail) || "Failed to load"))
      .finally(() => setLoading(false));
  };
  useEffect(load, [filter]); // eslint-disable-line

  if (loading || !data) return <div className="py-16 text-center text-bdu-cream/50">Loading login activity…</div>;

  return (
    <div className="space-y-5" data-testid="logins-pane">
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatTile label="Successful · Today" value={data.counts.today} testId="stat-logins-today" />
        <StatTile label="Successful · Last 7d" value={data.counts.week} testId="stat-logins-week" />
        <StatTile label="Failed Attempts · Today" value={data.counts.failed_today} accent="red" testId="stat-logins-failed" />
        <StatTile label="New Registrations · 7d" value={data.counts.registrations_week} accent="gold" testId="stat-logins-registrations" />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {[["all", "All"], ["login", "Logins"], ["login_failed", "Failed"], ["register", "Registrations"]].map(([k, l]) => (
          <button
            key={k}
            onClick={() => setFilter(k)}
            data-testid={`logins-filter-${k}`}
            className={`px-3 py-1.5 border text-[0.65rem] uppercase tracking-widest ${filter === k ? "border-bdu-gold text-bdu-gold" : "border-bdu-gold/20 text-bdu-cream/60 hover:text-bdu-cream"}`}
          >{l}</button>
        ))}
        <button onClick={load} className="ml-auto btn-ghost text-[0.65rem]" data-testid="logins-refresh">Refresh</button>
      </div>

      {data.events.length === 0 ? (
        <div className="border border-bdu-gold/10 py-14 text-center text-bdu-cream/50">No login activity recorded yet.</div>
      ) : (
        <div className="border border-bdu-gold/10 overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="bg-bdu-surface2 text-bdu-cream/60 uppercase tracking-widest text-xs">
              <tr>
                <th className="px-4 py-3">When</th>
                <th className="px-4 py-3">Event</th>
                <th className="px-4 py-3">Email</th>
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">IP</th>
                <th className="px-4 py-3">Device</th>
                <th className="px-4 py-3">Browser</th>
              </tr>
            </thead>
            <tbody>
              {data.events.map((e) => (
                <tr key={e.id} className="border-t border-bdu-gold/10 hover:bg-bdu-surface/40" data-testid={`login-row-${e.id}`}>
                  <td className="px-4 py-3 text-bdu-cream/80 whitespace-nowrap" title={fmtDate(e.ts)}>
                    <div className="text-bdu-cream text-sm">{relTime(e.ts)}</div>
                    <div className="text-[0.65rem] text-bdu-cream/40">{fmtDate(e.ts)}</div>
                  </td>
                  <td className="px-4 py-3"><KindBadge kind={e.kind} success={e.success} /></td>
                  <td className="px-4 py-3 text-bdu-cream break-all">{e.email || "—"}</td>
                  <td className="px-4 py-3 text-bdu-cream/70">{e.name || "—"}</td>
                  <td className="px-4 py-3 text-bdu-cream/60 font-mono text-xs">{e.ip || "—"}</td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center gap-2 text-bdu-cream/80 text-xs">
                      <DeviceIcon device={e.device} />
                      <span>{e.device || "—"}</span>
                    </span>
                  </td>
                  <td className="px-4 py-3 text-bdu-cream/70 text-xs">
                    {e.browser} <span className="text-bdu-cream/40">· {e.os}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// --- Email captures pane ---------------------------------------------------
function CapturesPane() {
  const [data, setData] = useState(null);
  const [search, setSearch] = useState("");
  const [source, setSource] = useState("");
  const [loading, setLoading] = useState(true);
  const [openEmail, setOpenEmail] = useState(null);

  const load = () => {
    setLoading(true);
    const params = {};
    if (search) params.search = search;
    if (source) params.source = source;
    api.get("/admin/visitors/captures", { params })
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail) || "Failed to load"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [source]); // eslint-disable-line

  const submit = (e) => { e.preventDefault(); load(); };

  if (loading && !data) return <div className="py-16 text-center text-bdu-cream/50">Loading captured emails…</div>;

  return (
    <div className="space-y-5" data-testid="captures-pane">
      <div className="grid sm:grid-cols-3 gap-4">
        <StatTile label="Unique Emails Captured" value={data?.counts?.total_emails || 0} testId="stat-emails-total" />
        <StatTile label="New Today" value={data?.counts?.new_today || 0} accent="gold" testId="stat-emails-today" />
        <StatTile label="New · Last 7d" value={data?.counts?.new_week || 0} testId="stat-emails-week" />
      </div>

      {/* Source breakdown chips */}
      {data?.source_breakdown?.length > 0 && (
        <div className="border border-bdu-gold/15 bg-bdu-surface p-4" data-testid="captures-sources">
          <div className="label-eyebrow mb-3">Where Emails Came From · Last 30 Days</div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setSource("")}
              className={`text-[0.65rem] uppercase tracking-widest px-3 py-1.5 border ${source === "" ? "border-bdu-gold text-bdu-gold" : "border-bdu-cream/20 text-bdu-cream/70 hover:text-bdu-cream"}`}
            >
              All ({data.source_breakdown.reduce((s, r) => s + r.count, 0)})
            </button>
            {data.source_breakdown.map((r) => (
              <button
                key={r.source}
                onClick={() => setSource(r.source)}
                className={`text-[0.65rem] uppercase tracking-widest px-3 py-1.5 border ${source === r.source ? "border-bdu-gold text-bdu-gold" : "border-bdu-cream/20 text-bdu-cream/70 hover:text-bdu-cream"}`}
                data-testid={`captures-source-${r.source}`}
              >
                {r.source.replace(/_/g, " ")} ({r.count})
              </button>
            ))}
          </div>
        </div>
      )}

      <form onSubmit={submit} className="flex gap-2 items-center">
        <div className="flex-1 flex items-center border border-bdu-gold/25 focus-within:border-bdu-gold">
          <Search size={16} className="ml-3 text-bdu-cream/50" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by email or name…"
            className="flex-1 bg-transparent px-3 py-2.5 text-bdu-cream text-sm outline-none"
            data-testid="captures-search-input"
          />
        </div>
        <button type="submit" className="btn-gold text-xs" data-testid="captures-search-btn">Search</button>
      </form>

      {(!data?.emails || data.emails.length === 0) ? (
        <div className="border border-bdu-gold/10 py-14 text-center text-bdu-cream/50">No emails captured yet. As soon as someone donates, buys, or signs up, they'll show here.</div>
      ) : (
        <div className="border border-bdu-gold/10 overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="bg-bdu-surface2 text-bdu-cream/60 uppercase tracking-widest text-xs">
              <tr>
                <th className="px-4 py-3">Email</th>
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">First Source</th>
                <th className="px-4 py-3">Sources</th>
                <th className="px-4 py-3 text-center">Events</th>
                <th className="px-4 py-3">First Seen</th>
                <th className="px-4 py-3">Last Seen</th>
              </tr>
            </thead>
            <tbody>
              {data.emails.map((e) => (
                <tr
                  key={e.id}
                  onClick={() => setOpenEmail(e.email)}
                  className="border-t border-bdu-gold/10 hover:bg-bdu-surface/40 cursor-pointer"
                  data-testid={`capture-row-${e.email}`}
                >
                  <td className="px-4 py-3 text-bdu-cream break-all">{e.email}</td>
                  <td className="px-4 py-3 text-bdu-cream/70">{e.latest_name || "—"}</td>
                  <td className="px-4 py-3"><SourceLabel source={e.first_source} /></td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1 max-w-[220px]">
                      {(e.sources || []).map((s) => <SourceLabel key={s} source={s} />)}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-center text-bdu-gold font-serif text-lg tabular-nums">{e.touch_count}</td>
                  <td className="px-4 py-3 text-bdu-cream/60 text-xs">{fmtDate(e.first_seen)}</td>
                  <td className="px-4 py-3 text-bdu-cream/80 text-xs" title={fmtDate(e.last_seen)}>{relTime(e.last_seen)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {openEmail && <EmailTimelineModal email={openEmail} onClose={() => setOpenEmail(null)} />}
    </div>
  );
}

// --- Anonymous visitor pane ------------------------------------------------
function AnonymousPane() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/admin/visitors/anonymous", { params: { days } })
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail) || "Failed to load"))
      .finally(() => setLoading(false));
  };
  useEffect(load, [days]); // eslint-disable-line

  const deviceTotals = useMemo(() => {
    if (!data) return { total: 0, desktop: 0, mobile: 0, tablet: 0 };
    const b = data.device_breakdown || {};
    const total = (b.desktop || 0) + (b.mobile || 0) + (b.tablet || 0);
    return { total, ...b };
  }, [data]);

  if (loading || !data) return <div className="py-16 text-center text-bdu-cream/50">Loading anonymous traffic…</div>;

  return (
    <div className="space-y-5" data-testid="anonymous-pane">
      <div className="flex items-center gap-2 flex-wrap">
        {[7, 30, 90].map((d) => (
          <button
            key={d}
            onClick={() => setDays(d)}
            data-testid={`anon-days-${d}`}
            className={`px-3 py-1.5 border text-[0.65rem] uppercase tracking-widest ${days === d ? "border-bdu-gold text-bdu-gold" : "border-bdu-gold/20 text-bdu-cream/60"}`}
          >Last {d} days</button>
        ))}
        <button onClick={load} className="ml-auto btn-ghost text-[0.65rem]" data-testid="anon-refresh">Refresh</button>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatTile icon={<Eye size={16} />} label="Views · Today" value={data.counts.views_today} sub={`${data.counts.unique_today} unique`} testId="stat-anon-today" />
        <StatTile icon={<Users size={16} />} label={`Views · Last ${days}d`} value={data.counts.views_period} sub={`${data.counts.unique_period} unique`} testId="stat-anon-period" />
        <StatTile icon={<Monitor size={16} />} label="Desktop Views" value={deviceTotals.desktop || 0} sub={pct(deviceTotals.desktop, deviceTotals.total)} testId="stat-anon-desktop" accent="gold" />
        <StatTile icon={<Smartphone size={16} />} label="Mobile Views" value={deviceTotals.mobile || 0} sub={pct(deviceTotals.mobile, deviceTotals.total)} testId="stat-anon-mobile" accent="gold" />
      </div>

      <div className="border border-bdu-gold/15 bg-bdu-surface p-6" data-testid="anon-sessions-panel">
        <div className="label-eyebrow mb-3">Recent Anonymous Sessions</div>
        <p className="text-xs text-bdu-cream/50 mb-4">Each row is one browsing session (grouped by cookie-less visitor id). No PII — just enough to see how people are engaging.</p>
        {data.sessions.length === 0 ? (
          <div className="py-10 text-center text-bdu-cream/50">No traffic in this window.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="text-bdu-cream/60 uppercase tracking-widest text-xs">
                <tr>
                  <th className="px-2 py-3">Last Activity</th>
                  <th className="px-2 py-3">Landing Path</th>
                  <th className="px-2 py-3 text-center">Views</th>
                  <th className="px-2 py-3">Referrer</th>
                  <th className="px-2 py-3">Device</th>
                  <th className="px-2 py-3">Session Length</th>
                </tr>
              </thead>
              <tbody>
                {data.sessions.map((s) => {
                  const durMs = new Date(s.last_seen).getTime() - new Date(s.first_seen).getTime();
                  const durMin = Math.max(0, Math.round(durMs / 60000));
                  return (
                    <tr key={s.session_id} className="border-t border-bdu-gold/10">
                      <td className="px-2 py-3 text-bdu-cream/80 whitespace-nowrap" title={fmtDate(s.last_seen)}>{relTime(s.last_seen)}</td>
                      <td className="px-2 py-3 text-bdu-cream truncate max-w-[220px]" title={s.last_path}>{s.last_path}</td>
                      <td className="px-2 py-3 text-center text-bdu-gold tabular-nums">{s.views}</td>
                      <td className="px-2 py-3 text-bdu-cream/60 text-xs truncate max-w-[180px]">{s.referrer || "direct"}</td>
                      <td className="px-2 py-3">
                        <span className="inline-flex items-center gap-2 text-bdu-cream/80 text-xs">
                          <DeviceIcon device={s.device} />
                          <span className="capitalize">{s.device}</span>
                        </span>
                      </td>
                      <td className="px-2 py-3 text-bdu-cream/70 text-xs">{durMin === 0 ? "< 1 min" : `${durMin} min`}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

const pct = (part, total) => total > 0 ? `${Math.round((part / total) * 100)}% of traffic` : "";

// --- Small reusable stat tile ---------------------------------------------
function StatTile({ icon, label, value, sub, testId, accent }) {
  const valColor = accent === "red" ? "text-bdu-red" : accent === "gold" ? "text-bdu-gold" : "text-bdu-cream";
  return (
    <div className="border border-bdu-gold/20 bg-bdu-surface p-5" data-testid={testId}>
      <div className="flex items-center gap-2 text-bdu-gold">
        {icon}
        <span className="label-eyebrow" style={{ fontSize: "0.6rem" }}>{label}</span>
      </div>
      <div className={`mt-4 font-serif text-4xl tabular-nums ${valColor}`}>{Number(value || 0).toLocaleString()}</div>
      {sub && <div className="mt-1 text-xs text-bdu-cream/50">{sub}</div>}
    </div>
  );
}

// --- Email timeline modal --------------------------------------------------
function EmailTimelineModal({ email, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/admin/visitors/captures/${encodeURIComponent(email)}/timeline`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [email]);

  const merged = useMemo(() => {
    if (!data) return [];
    const cap = (data.captures || []).map((e) => ({ ...e, _kind: "capture" }));
    const log = (data.logins || []).map((e) => ({ ...e, _kind: "login" }));
    return [...cap, ...log].sort((a, b) => new Date(b.ts) - new Date(a.ts));
  }, [data]);

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center px-4"
      onClick={onClose}
      data-testid="email-timeline-modal"
    >
      <div
        className="bg-bdu-bg border border-bdu-gold/30 max-w-3xl w-full max-h-[85vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 border-b border-bdu-gold/10 flex items-start justify-between">
          <div>
            <div className="label-eyebrow" style={{ fontSize: "0.6rem" }}>Timeline · {merged.length} events</div>
            <h3 className="mt-2 font-serif text-2xl text-bdu-cream break-all">{email}</h3>
            {data?.rollup?.latest_name && <div className="text-bdu-cream/70 text-sm mt-1">{data.rollup.latest_name}</div>}
          </div>
          <button onClick={onClose} className="text-bdu-cream/60 hover:text-bdu-cream" data-testid="email-timeline-close">
            <X size={20} />
          </button>
        </div>
        <div className="overflow-y-auto px-6 py-5">
          {loading ? (
            <div className="py-10 text-center text-bdu-cream/50">Loading timeline…</div>
          ) : merged.length === 0 ? (
            <div className="py-10 text-center text-bdu-cream/50">No events for this email.</div>
          ) : (
            <ol className="space-y-3">
              {merged.map((e, i) => (
                <li key={`${e._kind}-${e.id || i}`} className="border-l-2 border-bdu-gold/40 pl-4 py-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    {e._kind === "login" ? (
                      <KindBadge kind={e.kind} success={e.success} />
                    ) : (
                      <SourceLabel source={e.source} />
                    )}
                    <span className="text-bdu-cream/50 text-xs" title={fmtDate(e.ts)}>{relTime(e.ts)}</span>
                    {typeof e.amount_cents === "number" && e.amount_cents > 0 && (
                      <span className="text-bdu-gold text-xs tabular-nums">${(e.amount_cents / 100).toLocaleString()}</span>
                    )}
                  </div>
                  <div className="mt-2 text-xs text-bdu-cream/70 flex gap-3 flex-wrap">
                    <span className="inline-flex items-center gap-1"><DeviceIcon device={e.device} size={12} /> {e.device}</span>
                    <span>{e.browser} · {e.os}</span>
                    {e.ip && <span className="font-mono text-bdu-cream/50">{e.ip}</span>}
                  </div>
                  {e.metadata && Object.keys(e.metadata).length > 0 && (
                    <div className="mt-2 text-[0.7rem] text-bdu-cream/50 font-mono break-all">
                      {Object.entries(e.metadata).filter(([, v]) => v != null && v !== "").map(([k, v]) => (
                        <span key={k} className="mr-3">{k}: <span className="text-bdu-cream/70">{String(v)}</span></span>
                      ))}
                    </div>
                  )}
                </li>
              ))}
            </ol>
          )}
        </div>
      </div>
    </div>
  );
}
